import { useCommonServices } from '.'
import {
  isEligibleForLlamaForMac,
  isEligibleForLlamaForWindows,
} from '../../components/item/cta-button/llama/eligibility'
import { BrowserService } from '../services/browser'

export enum InstallType {
  Android = 'android',
  IPhone = 'ios',
  IPad = 'ipad',

  Windows = 'windows',
  Mac = 'mac',

  Chrome = 'chrome',
  Firefox = 'firefox',
  Safari = 'safari',
  EdgePreChromium = 'edge pre-chromium',
  EdgeChromium = 'edge chromium',
}

function isEligibleForInstall(install: InstallType, browserService: BrowserService): boolean {
  return installEligibilityMap[install](browserService)
}

const installEligibilityMap: Record<InstallType, (browserService: BrowserService) => boolean> = {
  [InstallType.Android]: (bs) => bs.isAndroid,
  [InstallType.IPad]: (bs) => bs.isIPad,
  [InstallType.IPhone]: (bs) => bs.isIOS && !bs.isIPad,

  [InstallType.Windows]: (bs) => isEligibleForLlamaForWindows(bs),
  [InstallType.Mac]: (bs) => isEligibleForLlamaForMac(bs),

  [InstallType.Chrome]: (bs) => bs.isChrome && !bs.isMobileAndroidOrIOS,
  [InstallType.EdgePreChromium]: (bs) => bs.isEdge && !bs.isEdgeChromium && !bs.isMobileAndroidOrIOS,
  [InstallType.EdgeChromium]: (bs) => bs.isEdgeChromium && !bs.isMobileAndroidOrIOS,
  [InstallType.Firefox]: (bs) => bs.isFirefox && !bs.isMobileAndroidOrIOS,
  [InstallType.Safari]: (bs) => bs.isSafari && !bs.isMobileAndroidOrIOS,
}

export const allInstalls = Object.values(InstallType) as InstallType[]

export const mobileInstallTypes = [InstallType.Android, InstallType.IPad, InstallType.IPhone]

export const llamaInstallTypes = [InstallType.Mac, InstallType.Windows]

export const extensionInstallTypes = [
  InstallType.Firefox,
  InstallType.Safari,
  InstallType.EdgeChromium,
  InstallType.EdgePreChromium,
  InstallType.Chrome,
]

export const installPageLinks: Record<InstallType, string> = {
  [InstallType.Android]: '/mobile',
  [InstallType.IPhone]: '/mobile',
  [InstallType.IPad]: '/mobile',

  [InstallType.Windows]: '/desktop/windows',
  [InstallType.Mac]: '/desktop/mac',

  [InstallType.Chrome]: '/browser/chrome',
  [InstallType.EdgeChromium]: '/browser/edge',
  [InstallType.EdgePreChromium]: '/browser/edge',
  [InstallType.Safari]: '/browser/safari',
  [InstallType.Firefox]: '/browser/firefox',
}

const displayNames: Record<InstallType, string> = {
  [InstallType.Android]: 'Android',
  [InstallType.IPhone]: 'iOS',
  [InstallType.IPad]: 'iPad',

  [InstallType.Windows]: 'Windows',
  [InstallType.Mac]: 'Mac',

  [InstallType.Chrome]: 'Chrome',
  [InstallType.EdgeChromium]: 'Microsoft Edge',
  [InstallType.EdgePreChromium]: 'Microsoft Edge',
  [InstallType.Safari]: 'Safari',
  [InstallType.Firefox]: 'Firefox',
}

export const getInstallDisplayName = (install: InstallType, browserService: BrowserService): string => {
  // Chrome-based browsers like Brave may have custom display names
  if (install === InstallType.Chrome && browserService.isChrome) {
    return browserService.browserDisplayName
  }

  return displayNames[install]
}

/**
 * @param installOptions Potential install options in the order you'd like them considered.
 * @returns First install option that user is eligible for, or `undefined`.
 */
export function useFirstEligibleInstall<T extends InstallType>(installOptions: T[]): T | undefined {
  const { browserService } = useCommonServices()

  for (let i = 0; i < installOptions.length; ++i) {
    const install = installOptions[i]
    if (isEligibleForInstall(install, browserService)) {
      return install
    }
  }

  return installOptions.find((install) => isEligibleForInstall(install, browserService))
}

export function useIsEligibleForInstall(install: InstallType): boolean {
  const { browserService } = useCommonServices()
  return isEligibleForInstall(install, browserService)
}

export function useIsEligibleForLlama(): boolean {
  const isEligibleWindows = useIsEligibleForInstall(InstallType.Windows)
  const isEligibleMac = useIsEligibleForInstall(InstallType.Mac)

  return isEligibleWindows || isEligibleMac
}

export function useIsEligibleForExtension(): boolean {
  const isEligibleForChrome = useIsEligibleForInstall(InstallType.Chrome)
  const isEligibleForEdge = useIsEligibleForInstall(InstallType.EdgeChromium)
  const isEligibleForFirefox = useIsEligibleForInstall(InstallType.Firefox)
  const isEligibleForSafari = useIsEligibleForInstall(InstallType.Safari)
  const isEligibleForEdgePreChromium = useIsEligibleForInstall(InstallType.EdgePreChromium)

  return (
    isEligibleForChrome ||
    isEligibleForEdge ||
    isEligibleForFirefox ||
    isEligibleForSafari ||
    isEligibleForEdgePreChromium
  )
}

export function useDefaultInstall(): InstallType | undefined {
  return useFirstEligibleInstall(allInstalls)
}
